import { baseApi } from "./baseApi";

export interface Transaction {
  id: string;
  orderId: string | null;
  userId: string;
  type: "PAYMENT" | "REFUND";
  status: "PENDING" | "SUCCESS" | "FAILED" | "CANCELLED";
  paymentProvider: string | null;
  paymentMethod: string;
  amount: number;
  currency: string;
  paymentReference: string | null;
  paymentTransactionId: string | null;
  metadata: any;
  failureReason: string | null;
  processedAt: string | null;
  createdAt: string;
  updatedAt: string;
  order?: {
    id: string;
    displayOrderId: string;
    total?: number;
  } | null;
  user?: {
    id: string;
    name: string;
    email: string;
    phone?: string | null;
  } | null;
}

export interface TransactionFilters {
  page?: number;
  limit?: number;
  status?: "PENDING" | "SUCCESS" | "FAILED" | "CANCELLED";
  type?: "PAYMENT" | "REFUND";
  paymentProvider?: string;
  orderId?: string;
  userId?: string;
  search?: string;
  startDate?: string;
  endDate?: string;
}

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface PaginatedTransactionsResponse {
  success: boolean;
  data: Transaction[];
  pagination: PaginationMeta;
  message?: string;
}

/**
 * Transaction API slice
 * Handles transaction management API calls (admin/staff only)
 */
export const transactionApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get all transactions with pagination and filters
    getAllTransactions: builder.query<PaginatedTransactionsResponse, TransactionFilters | void>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params?.page) searchParams.append("page", String(params.page));
        if (params?.limit) searchParams.append("limit", String(params.limit));
        if (params?.status) searchParams.append("status", params.status);
        if (params?.type) searchParams.append("type", params.type);
        if (params?.paymentProvider) searchParams.append("paymentProvider", params.paymentProvider);
        if (params?.orderId) searchParams.append("orderId", params.orderId);
        if (params?.userId) searchParams.append("userId", params.userId);
        if (params?.search) searchParams.append("search", params.search);
        if (params?.startDate) searchParams.append("startDate", params.startDate);
        if (params?.endDate) searchParams.append("endDate", params.endDate);
        const queryString = searchParams.toString();
        return {
          url: `/transactions${queryString ? `?${queryString}` : ""}`,
          credentials: "include",
        };
      },
      providesTags: ["Transaction"],
    }),

    // Get transaction by ID
    getTransactionById: builder.query<
      { success: boolean; data: Transaction; message?: string },
      string
    >({
      query: (id) => ({
        url: `/transactions/${id}`,
        credentials: "include",
      }),
      providesTags: (_result, _error, id) => [{ type: "Transaction", id }],
    }),

    // Get transactions by order ID
    getTransactionsByOrder: builder.query<
      { success: boolean; data: Transaction[]; message?: string },
      string
    >({
      query: (orderId) => ({
        url: `/transactions/order/${orderId}`,
        credentials: "include",
      }),
      providesTags: ["Transaction"],
    }),
  }),
});

export const {
  useGetAllTransactionsQuery,
  useGetTransactionByIdQuery,
  useGetTransactionsByOrderQuery,
} = transactionApi;
